import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import I18nProvider from 'next-translate/I18nProvider';
import 'intl-pluralrules'; // polyfill for Safari iOS
import axios from 'axios';

import * as config from '../../config';
import { langShorthand, buildGoogleJobsSchema } from '../../utils';
import backendLogger from '../../utils/backendLogger';

import Page404 from '../404';
import Spinner from '../../components/shared/spinner/Spinner';
import JobDescription from '../../components/description/JobDescription';

import ApproachContext from '../../context/ApproachContext';
import PublicConfigContext from '../../context/PublicConfigContext';

import commonEN from '../../../locales/en/common.json';
import commonDE from '../../../locales/de/common.json';
import commonES from '../../../locales/es/common.json';
import commonFR from '../../../locales/fr/common.json';
import { getGooglePlaceFromMemory } from '../../utils/google-place';

import styles from './job.module.scss';
import useRerender, { rerender } from '../../hooks/useRerender';

const NAMESPACES = {
  en: commonEN,
  de: commonDE,
  es: commonES,
  fr: commonFR,
};

// This part is here so we could change text from Google Optimize
global.i18nNamespaces = NAMESPACES;
global.rerender = rerender;

function Job({ job, company, slug, status, lang, schema, publicConfig }) {
  const router = useRouter();
  const { uid, viewpublicjob, salaryCheck, title, location, show_logs } =
    router.query;
  // This part of the code is ment to be used from Google Optimize to force rerender
  // when translations are changed.
  const rerenderData = useRerender(); // eslint-disable-line

  useEffect(() => {
    function getScrollPercent() {
      const h = document.documentElement;
      const b = document.body;
      const st = 'scrollTop';
      const sh = 'scrollHeight';
      return ((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight)) * 100;
    }

    window.scrollPercent = 0; // used for tracking purposes
    window.addEventListener(
      'scroll',
      () => {
        if (window.scrollPercent < getScrollPercent()) {
          window.scrollPercent = getScrollPercent();
        }
      },
      false,
    );

    // activation event for Google Optimize
    window.dataLayer?.push({ event: 'optimize.activate' });
  }, []);

  useEffect(() => {
    // Google Analytics
    if (!router.isFallback && window?.analytics?.user) {
      window.analytics.page('/Job Public Page');
    }
  }, [router.isFallback]);

  if (router.isFallback) {
    return <Spinner />;
  }

  if (!job || (status === 'job_closed' && !viewpublicjob)) {
    if (show_logs) {
      console.log('ERROR 82');
      console.log('JOB', job);
      console.log('STATUS', status);
      console.log('VIEWPUBLICJOB', viewpublicjob);
    }
    return <Page404 />;
  }

  let city = '';
  if (location) {
    city = decodeURIComponent(location.replaceAll('_', ' ')).split(',')[0];
  } else if (job.location?.name) {
    city = job.location.name.split(', ')[job.location.name.split.length - 1];
  }
  const jobTitle = title
    ? decodeURIComponent(title.replaceAll('_', ' '))
    : job?.opening_title;

  return (
    <I18nProvider lang={lang} namespaces={{ common: NAMESPACES[lang] }}>
      <main data-job-lang={lang} className={styles.main}>
        <Head>
          <title>{`Lionstep | ${jobTitle}`}</title>
          <meta
            property="og:title"
            content={`${
              job.extra_info?.can_be_disclosed
                ? `Job @ ${company.company_name}: `
                : ''
            }${jobTitle}`}
          />
          <meta
            property="og:description"
            content={`${
              job.extra_info?.can_be_disclosed
                ? company.company_name
                : 'Lionstep'
            } has a job opening for ${jobTitle} in ${city} | Apply now`}
          />
          <meta
            property="og:image"
            content={`${
              job.extra_info?.can_be_disclosed && company.logo_url
                ? company.logo_url
                : `${publicConfig.JOBS_URL}/static/Logo.svg`
            }`}
          />
          <meta property="og:type" content="website" />
          <meta
            property="og:url"
            content={`${publicConfig.JOBS_URL}/job/${slug}`}
          />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <link rel="shortcut icon" href="/static/favicon.ico" sizes="any" />
          <link rel="shortcut icon" href="/static/favicon.svg" />

          {/* don't index QA and Dev jobs, and don't index non-published Prod Jobs */}
          {(publicConfig.ENV !== 'production' || !status) && (
            <meta name="robots" content="noindex" />
          )}
          {schema && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
          )}
        </Head>

        <ApproachContext.Provider value={job.approach ?? 'formal'}>
          <PublicConfigContext.Provider value={publicConfig}>
            <JobDescription
              job={job}
              company={company}
              slug={slug}
              lang={lang}
              uid={uid}
              salaryCheck={salaryCheck}
            />
          </PublicConfigContext.Provider>
        </ApproachContext.Provider>
      </main>
    </I18nProvider>
  );
}

export async function getStaticProps({ params }) {
  const url = `${config.PYTHON_API}/api/v1/jobs/${params.slug}`;
  const startTime = new Date();
  let jobResponse;

  try {
    jobResponse = await axios.get(url);
  } catch (error) {
    backendLogger({
      ...error,
      additionalData: {
        url: `https://${process.env.HOSTNAME}/job/${params.slug}`,
        startTime,
        failTime: new Date(),
      },
    });
    // console.log('SSR ERROR', JSON.stringify(error?.response));
    if (error?.response?.status !== 404) throw error;
  }

  // console.log('SSR JOB', JSON.stringify(jobResponse?.data));

  if (!jobResponse) {
    return {
      props: {},
      revalidate: 1,
    };
  }

  const { job, company } = jobResponse.data;
  const lang = langShorthand(job.job_language.mid);

  let schema = null;

  if (
    job.extra_info?.can_be_disclosed &&
    job.status &&
    job.status !== 'job_closed' &&
    job.publish_to_job_board
  ) {
    let result = null;

    if (job.location) {
      try {
        result = (await getGooglePlaceFromMemory(job.location?.placeId, lang))
          .result;
      } catch (error) {
        console.log('PLACES ERROR', error);
        backendLogger(error);
      }
    }

    schema = await buildGoogleJobsSchema(
      lang,
      job,
      company,
      result?.address_components ?? [],
    );
  }

  return {
    props: {
      job,
      company,
      slug: params.slug,
      status: job.status,
      lang,
      schema,
      publicConfig: JSON.parse(JSON.stringify(config)),
    },
    revalidate: 1,
  };
}

export async function getStaticPaths() {
  let paths = [];

  try {
    let slugs;
    if (config.SSG_SLUGS) {
      slugs = config.SSG_SLUGS.split(',').map((slug) => ({
        slug: slug.trim(),
      }));
    } else {
      const { data } = await axios.get(
        `${config.PYTHON_API}/api/v1/jobs/active`,
      );

      slugs = data.results.filter(({ slug }) => slug);
    }

    paths = slugs.map(({ slug }) => ({ params: { slug } }));
  } catch (error) {
    console.log('STATIC ERROR', error);
    backendLogger(error);
  }

  return {
    paths,
    fallback: true,
  };
}

export default Job;
